import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialog as MatDialog, MatLegacyDialogConfig as MatDialogConfig, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { CollabFileType } from '@common/collab/enums/collab-file-type.enum';
import { CollabDocCommitEvent } from '@common/collab/events/collab-doc-commit.event';
import { CollabDocDeleteEvent } from '@common/collab/events/collab-doc-delete.event';
import { CollabDocMergeEvent } from '@common/collab/events/collab-doc-merge.event';
import { CollabDocRenameEvent } from '@common/collab/events/collab-doc-rename.event';
import { CollabDocRestoreEvent } from '@common/collab/events/collab-doc-restore.event';
import { cache } from '@common/util/cache.operator';
import { WorkspaceFileUserStatus } from '@common/workspace/enums/workspace-file-user-status.enum';
import { WorkspaceFile } from '@common/workspace/models/workspace-file.model';
import { BranchesService } from '@portal-core/branches/services/branches.service';
import { CollabCommitDialogComponent, CollabCommitDialogData } from '@portal-core/collab/components/collab-commit-dialog/collab-commit-dialog.component';
import { CollabEventSnackBarService } from '@portal-core/collab/services/collab-event-snack-bar.service';
import { CollabPullFetchService } from '@portal-core/collab/services/collab-pull-fetch.service';
import { CommitFileDialogComponent, CommitFileDialogData, CommitFileDialogResult } from '@portal-core/commits/components/commit-file-dialog/commit-file-dialog.component';
import { CurrentService } from '@portal-core/current/services/current.service';
import { ErrorService } from '@portal-core/errors/services/error.service';
import { LicenseUser } from '@portal-core/license-users/models/license-user.model';
import { FlareCollabEditorComponent } from '@portal-core/project-files/components/flare-collab-editor/flare-collab-editor.component';
import { ProjectFlareFileComponent } from '@portal-core/project-files/components/project-flare-file/project-flare-file.component';
import { ProjectFile } from '@portal-core/project-files/models/project-file.model';
import { ProjectFilesService } from '@portal-core/project-files/services/project-files.service';
import { FlareTextEditorType } from '@portal-core/project-files/types/flare-text-editor-type.type';
import { FlareXmlEditorMode } from '@portal-core/project-files/types/flare-xml-editor-mode.type';
import { WorkspaceFileService } from '@portal-core/project-files/workspace/services/workspace-file.service';
import { ProjectsService } from '@portal-core/projects/services/projects.service';
import { EditorChangeEvent } from '@portal-core/text-editor/types/editor-change-event.type';
import { DialogBase } from '@portal-core/ui/dialog/util/dialog.base';
import { SnackBarErrorComponent } from '@portal-core/ui/snack-bar/components/snack-bar-error/snack-bar-error.component';
import { AutoUnsubscribe } from '@portal-core/util/auto-unsubscribe.decorator';
import { LoadingState } from '@portal-core/util/loading-state';
import { BehaviorSubject, Observable, Subscription, catchError, distinctUntilChanged, map, of, switchMap, tap } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

export interface ProjectFilesEditSnippetDialogData {
  branchName: string;
  editorType: FlareTextEditorType;
  filePath: string;
  licenseUser: LicenseUser;
  navToFileCallback: (src: string) => void;
  projectId: number;
  projectLanguage: string;
  showConditions: boolean;
}

@Component({
  selector: 'mc-project-files-edit-snippet-dialog',
  templateUrl: './project-files-edit-snippet-dialog.component.html',
  styleUrls: ['./project-files-edit-snippet-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})

@AutoUnsubscribe()
export class ProjectFilesEditSnippetDialogComponent extends DialogBase implements OnInit, OnDestroy {
  static DialogConfig: MatDialogConfig = {
    width: '110rem',
    height: '65rem'
  };

  @ViewChild('projectFlareFileEditor', { static: false }) projectFlareFileEditor: ProjectFlareFileComponent;
  @ViewChild('flareCollabEditor', { static: false }) flareCollabEditor: FlareCollabEditorComponent;

  CollabFileType: typeof CollabFileType = CollabFileType;

  collabCommitClientId: string;
  commitId$: Observable<string>;
  editorType: FlareTextEditorType;
  fileMode: FlareXmlEditorMode = 'text';
  lastCommitId: string;
  licenseId: number;
  loadingState: LoadingState<string> = new LoadingState();
  projectFile$: Observable<ProjectFile>;
  readonly: boolean = false;
  reload$: BehaviorSubject<void> = new BehaviorSubject(undefined);
  snippetIsDirty: boolean = false;
  trackedChanges: boolean = false;
  workspaceFile$: Observable<WorkspaceFile>;
  codeEditorPermissionsError: string;
  contentEditorPermissionsError: string;
  codeEditorErrorPermissionsSubscription: Subscription;
  wrapLines: boolean = true;

  constructor(
    protected dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: ProjectFilesEditSnippetDialogData,
    protected dialogRef: MatDialogRef<ProjectFilesEditSnippetDialogComponent>,
    private branchesService: BranchesService,
    private collabEventSnackBarService: CollabEventSnackBarService,
    private collabPullFetchService: CollabPullFetchService,
    private currentService: CurrentService,
    private projectFilesService: ProjectFilesService,
    private errorService: ErrorService,
    private snackBar: MatSnackBar,
    private workspaceFileService: WorkspaceFileService,
    private projectsService: ProjectsService,
    private cdr: ChangeDetectorRef
  ) {
    super(dialog, dialogRef);
  }

  ngOnInit() {
    super.ngOnInit();
    this.init();
    this.codeEditorErrorPermissionsSubscription = this.projectsService.getAccessErrorMessages$(this.data.projectId).subscribe((errorMessages) => {
      this.codeEditorPermissionsError = errorMessages?.codeEditorPermissionsError;
      this.contentEditorPermissionsError = errorMessages?.contentEditorPermissionsError;
      this.cdr.markForCheck();
    })
  }

  ngOnDestroy() {
    // Remove the file from the pull or fetch list if in collab mode
    if (this.editorType === 'collab') {
      this.collabPullFetchService.removePullOrFetchFile(this.data.projectId, this.data.branchName, this.data.filePath);
    }
  }

  get requirePromptOnClose(): boolean {
    return this.editorType === 'solo' && this.snippetIsDirty;
  }

  onFlareEditModeClicked(event: MouseEvent, mode: FlareXmlEditorMode) {
    event.preventDefault();

    if (this.projectFlareFileEditor.canChangeMode(mode)) {
      this.fileMode = mode;
    }
  }

  onFlareCollabFileModeClicked(mode: FlareXmlEditorMode) {
    this.fileMode = mode;
  }

  onFlareFileModeChanged(mode: FlareXmlEditorMode) {
    this.fileMode = mode;
  }

  // Content has been changed so now you can commit
  onContentChange($event: EditorChangeEvent) {
    // !$event is for events from project-code-file, they are null and only run when a user changes contents, which is not true for mc-project-flare-file hence why !$event and $event.dirty  are needed.
    if (!$event || $event.dirty) {
      this.setSnippetIsDirty(true);
    }
  }

  private setSnippetIsDirty(snippetIsDirty: boolean) {
    this.snippetIsDirty = snippetIsDirty;
  }

  onCollabDocCommitted(event: CollabDocCommitEvent) {
    this.collabEventSnackBarService.onCollabDocCommitted(event, this.flareCollabEditor?.docId);
  }

  onCollabDocDeleted(event: CollabDocDeleteEvent) {
    this.collabEventSnackBarService.onCollabDocDeleted(event, this.flareCollabEditor?.docId);

    // Close the dialog if the deleted doc is open in the dialog
    if (this.flareCollabEditor?.docId === event.docId) {
      this.closeDialog(null, true);
    }
  }

  onCollabDocMerged(event: CollabDocMergeEvent) {
    this.collabEventSnackBarService.onCollabDocMerged(event, this.flareCollabEditor?.docId);
  }

  onCollabDocRenamed(event: CollabDocRenameEvent) {
    this.collabEventSnackBarService.onCollabDocRenamed(event, this.flareCollabEditor?.docId);

    // Re-init for the new file path if the renamed doc is open in the dialog
    if (this.flareCollabEditor?.docId === event.oldDocId) {
      // Remove the old file from the pull or fetch list
      this.collabPullFetchService.removePullOrFetchFile(this.data.projectId, this.data.branchName, this.data.filePath);

      // Update the data for the new path and re-init
      this.data.filePath = event.newFilePath;
      this.init();
    }
  }

  onCollabDocRestored(event: CollabDocRestoreEvent) {
    this.collabEventSnackBarService.onCollabDocRestored(event, this.flareCollabEditor?.docId);
  }

  onCommitClicked() {
    if (this.editorType === 'solo') {
      this.commitSoloFile();
    } else {
      this.commitCollabFile();
    }
  }

  updateWorkspaceFileUserStatus(workspaceFile: WorkspaceFile, status: WorkspaceFileUserStatus, docVersion: number) {
    this.workspaceFileService.bulkUpdateWorkspaceFileStatus$(this.data.projectId, [workspaceFile.docId], [workspaceFile.schemaName], this.data.licenseUser.User.Id, status, [docVersion]).subscribe({
      error: () => {
        this.snackBar.openFromComponent(SnackBarErrorComponent, {
          data: 'There was a problem updating the user status.'
        });
      }
    });
  }

  private commitCollabFile() {
    this.collabCommitClientId = uuidv4();

    this.dialog.open<CollabCommitDialogComponent, CollabCommitDialogData>(CollabCommitDialogComponent, {
      ...CollabCommitDialogComponent.DialogConfig,
      data: {
        branchName: this.data.branchName,
        clientId: this.collabCommitClientId,
        filePaths: [this.data.filePath],
        licenseId: this.licenseId,
        projectId: this.data.projectId,
        userId: this.data.licenseUser.User.Id
      }
    }).afterClosed().subscribe(result => {
      if (result) {
        this.closeDialog(result, true);
      }
    });
  }

  private commitSoloFile() {
    const content = this.getFileContent();

    if (typeof content === 'string') {
      // Open commit dialog
      this.dialog.open<CommitFileDialogComponent, CommitFileDialogData, CommitFileDialogResult>(CommitFileDialogComponent, {
        ...CommitFileDialogComponent.DialogConfig,
        data: {
          branchName: this.data.branchName,
          content,
          filePath: this.data.filePath,
          lastCommitId: this.lastCommitId,
          newFile: false,
          projectId: this.data.projectId
        }
      }).afterClosed().subscribe(result => {
        if (result) {
          this.closeDialog(result, true);
        }
      });
    }
  }

  private getFileContent(): string {
    return this.projectFlareFileEditor.getFormattedContent();
  }

  protected onNavToFile(src: string) {
    this.closeDialog();
    this.data.navToFileCallback(src);
  }

  private init() {
    this.editorType = this.data.editorType ?? 'solo';
    this.licenseId = this.currentService.getActiveLicenseId();

    if (this.editorType === 'collab') {
      // Add the file to the pull or fetch list
      this.collabPullFetchService.addPullOrFetchFile(this.data.projectId, this.data.branchName, this.data.filePath);

      // Create an observable for the collab doc's workspace file. The workspace session will load the data so don't allow an API request.
      this.workspaceFile$ = this.workspaceFileService.getWorkspaceFile$(this.data.projectId, this.data.branchName, this.data.filePath, { allowApiRequest: false }).pipe(
        cache()
      );

      // Create an observable for the project's latest commit id
      this.commitId$ = this.branchesService.getBranchFromProjectId$(this.data.projectId, this.data.branchName).pipe(
        map(branch => branch?.Commit?.id),
        distinctUntilChanged(),
        cache()
      );
    } else {
      this.loadingState.update(true);

      // Create an observable for the project file
      this.projectFile$ = this.reload$.pipe(
        switchMap(() => {
          this.loadingState.update(true);
          return this.projectFilesService.getProjectFile$(this.data.projectId, this.data.filePath, this.data.branchName).pipe(
            tap(projectFile => {
              this.lastCommitId = projectFile.LastCommitId;
              this.loadingState.update(false);
            })
          );
        }),
        catchError(error => {
          this.loadingState.update(false, 'Unable to load the snippet file.', this.errorService.getErrorMessages(error));
          return of(null);
        })
      );
    }
  }
}

